import React from "react";
import { useI18n } from "@/i18n";

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const PRIORITY_COLORS = {
  High: "#F43F5E",
  Medium: "#F59E0B",
  Low: "#10B981",
};

export function ProgramTimelineGantt({ programs }) {
  const { t } = useI18n();
  if (!programs || programs.length === 0) return null;

  const tierName = (p) => {
    const key = `tier.${p.tier_key}`;
    const v = t(key);
    return v === key ? p.tier_label || "" : v;
  };

  return (
    <div className="w-full overflow-x-auto" data-testid="program-gantt">
      <div className="min-w-[720px]">
        <div className="flex items-center border-b border-slate-200 pb-2 mb-3">
          <div className="w-44 shrink-0 text-xs uppercase tracking-widest font-semibold text-amber-600">
            {t("gantt.program")}
          </div>
          <div className="flex-1 grid grid-cols-12">
            {MONTHS.map((m) => (
              <div key={m} className="text-center text-[10px] font-mono text-slate-500">
                M{m}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {programs.map((p, idx) => {
            const start = Math.max(1, Math.min(12, p.start_month || 1));
            const dur = Math.max(1, Math.min(12 - start + 1, p.duration_months || 3));
            const leftPct = ((start - 1) / 12) * 100;
            const widthPct = (dur / 12) * 100;
            const color = PRIORITY_COLORS[p.priority] || "#6366F1";
            return (
              <div key={idx} className="flex items-center" data-testid={`gantt-row-${p.tier_key}`}>
                <div className="w-44 shrink-0 pr-3">
                  <p className="text-sm font-medium text-slate-800 truncate">{tierName(p)}</p>
                  <p className="text-[11px] text-slate-500 truncate">{p.program_name}</p>
                </div>
                <div className="flex-1 relative h-9 rounded-md bg-slate-50 border border-slate-200/60">
                  <div className="absolute inset-0 grid grid-cols-12">
                    {MONTHS.map((m) => (
                      <div key={m} className="border-r border-slate-200/40 last:border-0" />
                    ))}
                  </div>
                  <div
                    className="absolute top-1 bottom-1 rounded-md flex items-center px-2 shadow-lg"
                    style={{
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      background: `${color}22`,
                      border: `1px solid ${color}`,
                    }}
                    title={`${p.program_name} — ${t("gantt.bar", { months: dur, format: p.format })}`}
                  >
                    <span className="text-[10px] font-mono truncate" style={{ color }}>
                      {t("gantt.bar", { months: dur, format: p.format })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-200">
          {Object.entries(PRIORITY_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: v }} />
              <span className="text-xs text-slate-500">{t("gantt.legend", { priority: t(`priority.${k}`) })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
